import { collection, addDoc, serverTimestamp, FirestoreError } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { toast } from '@/lib/toast';
import { AchievementDefinition, EarnedAchievement } from '@/models/Achievement';
import { ActivityLog } from '@/models/LearnTypes';
import { profileService, UserProfile } from './ProfileService';
import { activityService } from './ActivityService';
import { achievements } from '@/services/achievements/AchievementRegistry';

class AchievementService {
  private collectionName = 'activityLogs'; // Using existing activityLogs collection that has security rules

  /**
   * Check all possible achievements for a user and award any newly earned ones
   * @param userId User ID to check achievements for
   * @returns Array of newly earned achievement IDs
   */
  async checkAllAchievements(userId: string): Promise<string[]> {
    console.log('🏆 [Achievement] Starting achievement check for user:', userId);
    
    try {
      // Get user profile
      const user = await profileService.getUserProfile(userId);
      if (!user) {
        console.error('❌ [Achievement] Cannot check achievements: User profile not found');
        return [];
      }

      console.log('🏆 [Achievement] User profile loaded successfully');

      // Get user activities
      const activities = await activityService.getUserActivities(userId);
      console.debug(`🏆 [Achievement] Found ${activities.length} activities for achievement checking`);
      
      // Get already earned achievements
      const existingAchievements = user.achievements || [];
      const earnedAchievementIds = new Set(
        existingAchievements.map(achievement => achievement.id)
      );
      
      if (existingAchievements.length > 0) {
        console.log(`🏆 [Achievement] User has ${existingAchievements.length} achievements:`, 
          existingAchievements.map(a => a.id).join(', '));
      } else {
        console.log('🏆 [Achievement] User has no existing achievements');
      }
      
      // Check each achievement
      const newlyEarnedIds: string[] = [];
      console.log(`🏆 [Achievement] Checking ${achievements.length} possible achievements`);
      
      // Maintain a list of achievements we're processing in this session
      // to prevent awarding the same achievement twice in a single check
      const processedAchievements = new Set<string>();
      
      for (const achievement of achievements) {
        // Skip if already earned or already processed in this session
        if (earnedAchievementIds.has(achievement.id)) {
          console.log(`⏩ [Achievement] Skipping already earned: ${achievement.id} - ${achievement.title}`);
          continue;
        }
        
        if (processedAchievements.has(achievement.id)) {
          console.log(`⏩ [Achievement] Already processed in this session: ${achievement.id}`);
          continue;
        }
        
        // Mark as processed to prevent duplicates within this check
        processedAchievements.add(achievement.id);
        
        // Check if achievement condition is met
        const isEarned = achievement.condition(user, activities);
        
        if (isEarned) {
          console.debug(`🎯 [Achievement] EARNED: ${achievement.id} - ${achievement.title}`);
          const awarded = await this.awardAchievement(userId, achievement.id);
          
          if (awarded) {
            newlyEarnedIds.push(achievement.id);
            console.log(`✅ [Achievement] Successfully awarded: ${achievement.id}`);
          } else {
            console.warn(`⚠️ [Achievement] Failed to award: ${achievement.id}`);
          }
        }
      }
      
      if (newlyEarnedIds.length > 0) {
        console.log(`🏆 [Achievement] Newly earned ${newlyEarnedIds.length} achievements: ${newlyEarnedIds.join(', ')}`);
      } else {
        console.log('🏆 [Achievement] No new achievements earned in this check');
      }
      
      return newlyEarnedIds;
    } catch (error) {
      console.error('❌ Error checking achievements:', error);
      return [];
    }
  }
  
  /**
   * Award a specific achievement to a user
   * @param userId User ID to award achievement to
   * @param achievementId Achievement ID to award
   * @returns Whether the achievement was successfully awarded
   */
  async awardAchievement(userId: string, achievementId: string): Promise<boolean> {
    console.log(`🏆 [Achievement] Awarding achievement ${achievementId} to user ${userId}`);
    
    try {
      // Find the achievement definition
      const achievement = achievements.find(a => a.id === achievementId);
      if (!achievement) {
        console.error(`❌ [Achievement] Cannot award achievement: Definition not found for ${achievementId}`);
        return false;
      }
      
      console.log(`🏆 [Achievement] Found definition for ${achievementId}: ${achievement.title}`);
      
      // Create earned achievement record
      const earnedAt = new Date();
      const earnedAchievement: EarnedAchievement = {
        id: achievementId,
        earnedAt,
        viewed: false
      };
      
      // Add to user profile - this now includes duplicate checking
      console.debug(`🏆 [Achievement] Adding to user profile: ${achievementId}`);
      const added = await profileService.addAchievement(userId, earnedAchievement);
      
      if (!added) {
        console.warn(`⚠️ [Achievement] Failed to add achievement to profile: ${achievementId}`);
        return false;
      }
      
      // Log activity
      console.log(`🏆 [Achievement] Logging achievement activity: ${achievementId}`);
      await this.logAchievementActivity(userId, achievement);
      
      // Award XP bonus if applicable
      if (achievement.xpReward && achievement.xpReward > 0) {
        await this.awardXPBonus(userId, achievement);
      }
      
      return true;
    } catch (error) {
      console.error('❌ Error awarding achievement:', error);
      return false;
    }
  }
  
  /**
   * Log an achievement earned activity
   * @param userId User ID
   * @param achievement Achievement definition
   */
  private async logAchievementActivity(
    userId: string,
    achievement: AchievementDefinition
  ): Promise<string | null> {
    try {
      console.debug(`📝 Logging achievement activity: ${achievement.title}`);
      
      const activityData = {
        userId,
        type: 'achievement-earned' as const,
        achievementId: achievement.id,
        achievementTitle: achievement.title,
        timestamp: serverTimestamp(),
        xpEarned: achievement.xpReward || 0
      };
      
      // Log to activity collection
      const docRef = await addDoc(collection(db, this.collectionName), activityData);
      return docRef.id;
    } catch (error) {
      const firestoreError = error as FirestoreError;
      console.error('❌ Error logging achievement activity:', firestoreError);
      return null;
    }
  }
  
  /**
   * Award XP bonus for achievement
   * @param userId User ID
   * @param achievement Achievement definition
   */
  private async awardXPBonus(
    userId: string,
    achievement: AchievementDefinition
  ): Promise<boolean> {
    try {
      if (!achievement.xpReward || achievement.xpReward <= 0) {
        return false;
      }
      
      console.debug(`💰 Awarding XP bonus for achievement: ${achievement.xpReward} XP`);
      
      const user = await profileService.getUserProfile(userId);
      if (!user) return false;
      
      const newXP = user.xp + achievement.xpReward;
      
      // Update user profile with new XP
      return await profileService.updateUserProfile(userId, { xp: newXP });
    } catch (error) {
      console.error('❌ Error awarding XP bonus:', error);
      return false;
    }
  }
  
  /**
   * Get achievement definition by ID
   * @param achievementId Achievement ID
   * @returns Achievement definition or null if not found
   */
  getAchievementById(achievementId: string): AchievementDefinition | null {
    return achievements.find(a => a.id === achievementId) || null;
  }
  
  /**
   * Get all achievement definitions
   * @returns Array of all achievement definitions
   */
  getAllAchievements(): AchievementDefinition[] {
    return [...achievements];
  }
}

export const achievementService = new AchievementService();
